import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiMenu, FiX, FiList } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import Logo from './Logo';
import LanguageSelector from './LanguageSelector';
import ThemeToggle from './ThemeToggle';
import { appsData } from '../data/appsData';

export default function Navbar() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [showAlpha, setShowAlpha] = useState(false);

  const sortedApps = [...appsData].sort((a, b) => 
    t(`apps.${a.id}.title`).localeCompare(t(`apps.${b.id}.title`))
  );

  return (
    <nav className="sticky top-0 z-[60] w-full bg-white/80 dark:bg-[#0F172A]/80 backdrop-blur-xl border-b border-gray-200 dark:border-gray-800">
      <div className="max-w-7xl mx-auto px-4 h-20 flex justify-between items-center">
        <div className="flex flex-col">
          <Link to="/" className="flex items-center">
            <Logo className="text-3xl" />
          </Link>
          <span className="text-[10px] md:text-[11px] font-bold text-black dark:text-white tracking-tighter mt-0.5">
            {t('common.slogan')}
          </span>
        </div>

        <div className="hidden lg:flex items-center gap-6">
          <div className="relative">
            <button 
              onClick={() => setShowAlpha(!showAlpha)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/5 transition-all"
            >
              <FiList /> {t('common.alphabetical')}
            </button>
            <AnimatePresence>
              {showAlpha && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }} className="absolute top-full right-0 mt-2 w-64 bg-white dark:bg-[#1E293B] border border-gray-200 dark:border-gray-800 rounded-2xl shadow-2xl py-2 max-h-96 overflow-y-auto">
                  {sortedApps.map(app => (
                    <a key={app.id} href={app.url} target="_blank" rel="noopener noreferrer" className="block px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-blue-600/10 hover:text-blue-600 transition-colors">
                      {t(`apps.${app.id}.title`)}
                    </a>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <div className="h-6 w-px bg-gray-200 dark:bg-gray-800" />
          <LanguageSelector />
          <ThemeToggle />
        </div>

        <button className="lg:hidden p-2 rounded-xl bg-gray-100 dark:bg-white/5 text-gray-900 dark:text-white" onClick={() => setIsOpen(!isOpen)}>
          {isOpen ? <FiX size={24} /> : <FiMenu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="lg:hidden bg-white dark:bg-[#1E293B] border-b border-gray-200 dark:border-gray-800 overflow-hidden">
            <div className="p-4 space-y-4">
              <div className="flex justify-between items-center pb-4 border-b border-gray-100 dark:border-gray-800">
                <LanguageSelector />
                <ThemeToggle />
              </div>
              <p className="text-xs font-bold text-gray-400 uppercase px-4">{t('common.alphabetical')}</p>
              <div className="grid grid-cols-1 gap-1">
                {sortedApps.map(app => (
                  <a key={app.id} href={app.url} target="_blank" rel="noopener noreferrer" className="block p-4 rounded-xl text-gray-700 dark:text-gray-200 font-bold hover:bg-gray-50 dark:hover:bg-white/5">
                    {t(`apps.${app.id}.title`)}
                  </a>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}